import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { IPackageItem } from 'qbe-components';
import { BehaviorSubject, Observable, Subject, combineLatest, map, takeUntil } from 'rxjs';
import { ApiState } from 'src/app/model/api-state.model';
import { FormsResignation, FormsTravelInPl, FormsTripAbroad } from 'src/app/model/forms.model';
import { PremiumCalculationApi, QuotationAddon, QuotationPackage } from 'src/app/model/offer';
import { ProcessType } from 'src/app/model/process-type.model';
import { FormsService } from 'src/app/services/forms.service';
import { PersistStorageKeys, PersistStorageService } from 'src/app/services/persist-storage.service';
import { ProcessService } from 'src/app/services/process.service';
import { AppState } from 'src/app/store';
import { dateFormatter, formatterCurrency, getCountryZone, objectGetProp } from 'src/app/utils';
import * as fromConfig from '../../../store/config/config.reducer';
import * as fromProcess from '../../../store/process/process.reducer';
import isAppAgentOrTravelAgency from '../../../utils/is-agent-or-travel-agency.util';
import { SwitcherStates } from '../../offer-content/offer-content.component';
import { environment } from '../../../environments/environment';
import { AppType } from '../../../model/app-type.model';

const ZERO = 0;
const FOUR_PACKAGES = 4;
const THREE_PACKAGES = 3;
const TWO_PACKAGES = 2;
const ONE_PACKAGE = 1;

interface AgentInfo {
  agentNumber: string | null;
  agentFirstName: string | null;
  agentLastName: string | null;
  agentPhone: string | null;
  agentMail: string | null;
}
interface InformationsAboutJourneyView {
  title: string;
  value: string | undefined;
}

interface SummaryList {
  comfort?: { label: string };
  plus?: { label: string };
  extra?: { label: string };
  max?: { label: string };
}

interface PackageTableProps {
  labelComfort?: string;
  labelPlus?: string;
  labelExtra?: string;
  labelMax?: string;
  packageList: IPackageItem[];
  packageListSummary: SummaryList;
  addonsList: IPackageItem[];
  addonsListSummary: SummaryList;
}

@Component({
  selector: 'app-offer-leaflet',
  templateUrl: './offer-leaflet.component.html',
  styleUrls: ['./offer-leaflet.component.scss'],
})
export class OfferLeafletComponent implements OnInit, OnDestroy {
  processKind$ = this.store.select(fromProcess.selectProcessKind);
  translations$ = this.translateService.getTranslation(this.translateService.currentLang);
  selectedTab$ = this.store.select(fromProcess.selectOfferSelectedPackageIndex);
  premiumCalculationState$ = this.store.select(fromProcess.selectPremiumCalculationState);
  premiumCalculationData$ = this.store.select(fromProcess.selectPremiumCalculationData);
  packageAddons$ = this.store.select(fromProcess.selectPackageAddons);
  packages$ = combineLatest([
    this.store.select(fromProcess.selectOfferQuotationData),
    this.store.select(fromProcess.selectProcessKind),
  ]).pipe(map(([data, processKind]) => (processKind ? data?.result.path[processKind]?.packages || [] : [])));
  informationsAboutJourney: InformationsAboutJourneyView[] = [];
  currentPackage$ = combineLatest([this.selectedTab$, this.packages$]).pipe(map(([selectedTab, packages]) => packages[selectedTab]));
  offerDateParams$ = new BehaviorSubject<{ dateFrom: string | null; dateTo: string | null }>({ dateFrom: '—', dateTo: '—' });
  calculationDate = dateFormatter(new Date());
  packageColumnsWidth = '2';
  packageHeaderColumnsWidth = '2';
  numberOfPackages = Array(FOUR_PACKAGES).fill(ZERO);
  agentInfo: AgentInfo = {
    agentNumber: this.persistStorage.getString(PersistStorageKeys.AgentNumber),
    agentFirstName: this.persistStorage.getString(PersistStorageKeys.GivenName),
    agentLastName: this.persistStorage.getString(PersistStorageKeys.FamilyName),
    agentMail: this.persistStorage.getString(PersistStorageKeys.AgentMail),
    agentPhone: this.persistStorage.getString(PersistStorageKeys.AgentPhone),
  };
  isAppAgentOrTravelAgency = isAppAgentOrTravelAgency();
  isAppTravelAgency = environment.app === AppType.TravelAgency;
  ngDestroy$ = new Subject<void>();

  packageTableProps$: Observable<PackageTableProps> = combineLatest([
    this.packages$,
    this.premiumCalculationState$,
    this.premiumCalculationData$,
    this.packageAddons$,
  ]).pipe(
    map(([packages, premiumCalculationState, premiumCalculation, packageAddons]) => {
      const [comfort, plus, extra, max] = packages;
      const packageListElements = Math.max(
        comfort?.coverages?.length || ZERO,
        plus?.coverages?.length || ZERO,
        extra?.coverages?.length || ZERO,
        max?.coverages?.length || ZERO,
      );

      const packageList: IPackageItem[] = Array(packageListElements)
        .fill(null)
        .map((_, index) => {
          const coverage = comfort?.coverages?.[index];
          const coverageComfort = comfort?.coverages?.[index];
          const coveragePlus = plus?.coverages?.[index];
          const coverageExtra = extra?.coverages?.[index];
          const coverageMax = max?.coverages?.[index];

          if (!coverage) {
            throw new Error('compare-offers.component.ts: packageList.map: coverage is null');
          }

          return {
            title: coverage.name,
            comfort: coverageComfort && {
              value: coverageComfort.enabled,
              label: coverageComfort.limit ? formatterCurrency(coverageComfort.limit.amount, coverageComfort.limit.currency) : '',
            },
            plus: coveragePlus && {
              value: coveragePlus.enabled,
              label: coveragePlus.limit ? formatterCurrency(coveragePlus.limit.amount, coveragePlus.limit.currency) : '',
            },
            extra: coverageExtra && {
              value: coverageExtra.enabled,
              label: coverageExtra.limit ? formatterCurrency(coverageExtra.limit.amount, coverageExtra.limit.currency) : '',
            },
            max: coverageMax && {
              value: coverageMax.enabled,
              label: coverageMax.limit ? formatterCurrency(coverageMax.limit.amount, coverageMax.limit.currency) : '',
            },
          };
        });

      const packageListSummary = {
        comfort: comfort && {
          label: comfort.totalValue.amount ? formatterCurrency(comfort.totalValue.amount, comfort.totalValue.currency) : '',
        },
        plus: plus && {
          label: plus.totalValue.amount ? formatterCurrency(plus.totalValue.amount, plus.totalValue.currency) : '',
        },
        extra: extra && {
          label: extra.totalValue.amount ? formatterCurrency(extra.totalValue.amount, extra.totalValue.currency) : '',
        },
        max: max && {
          label: max.totalValue.amount ? formatterCurrency(max.totalValue.amount, max.totalValue.currency) : '',
        },
      };

      const addonsListElements = Math.max(
        comfort?.addOns?.length || ZERO,
        plus?.addOns?.length || ZERO,
        extra?.addOns?.length || ZERO,
        max?.addOns?.length || ZERO,
      );

      const addonsList: IPackageItem[] = Array(addonsListElements)
        .fill(null)
        .map((_, index) => {
          const addons = comfort?.addOns?.[index];
          const addonsComfort = comfort?.addOns?.[index];
          const addonsPlus = plus?.addOns?.[index];
          const addonsExtra = extra?.addOns?.[index];
          const addonsMax = max?.addOns?.[index];

          if (!addons) {
            throw new Error('compare-offers.component.ts: packageList.map: coverage is null');
          }

          return {
            title: addons.name,
            comfort: addonsComfort && {
              value: this.isAddonActive(addonsComfort, packageAddons),
              label: addonsComfort.premium ? formatterCurrency(addonsComfort.premium.amount, addonsComfort.premium.currency) : '',
            },
            plus: addonsPlus && {
              value: this.isAddonActive(addonsPlus, packageAddons),
              label: addonsPlus.premium ? formatterCurrency(addonsPlus.premium.amount, addonsPlus.premium.currency) : '',
            },
            extra: addonsExtra && {
              value: this.isAddonActive(addonsExtra, packageAddons),
              label: addonsExtra.premium ? formatterCurrency(addonsExtra.premium.amount, addonsExtra.premium.currency) : '',
            },
            max: addonsMax && {
              value: this.isAddonActive(addonsMax, packageAddons),
              label: addonsMax.premium ? formatterCurrency(addonsMax.premium.amount, addonsMax.premium.currency) : '',
            },
          };
        });

      const addonsListSummary = {
        comfort: comfort && {
          label: this.getPremiumValue(comfort, premiumCalculationState, premiumCalculation),
        },
        plus: plus && {
          label: this.getPremiumValue(plus, premiumCalculationState, premiumCalculation),
        },
        extra: extra && {
          label: this.getPremiumValue(extra, premiumCalculationState, premiumCalculation),
        },
        max: max && {
          label: this.getPremiumValue(max, premiumCalculationState, premiumCalculation),
        },
      };

      return {
        labelComfort: comfort?.name,
        labelPlus: plus?.name,
        labelExtra: extra?.name,
        labelMax: max?.name,
        packageList,
        packageListSummary,
        addonsList,
        addonsListSummary,
      };
    }),
  );

  constructor(
    private store: Store<AppState>,
    public formsService: FormsService,
    private translateService: TranslateService,
    private persistStorage: PersistStorageService,
    private processService: ProcessService,
  ) {}

  ngOnInit(): void {
    this.prepareCalculationInfo();
    this.calculateColumnsWidth();
  }

  ngOnDestroy(): void {
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }

  prepareCalculationInfo() {
    combineLatest([
      this.processKind$,
      this.translations$,
      this.store.select(fromConfig.selectConfigTripAbroadCountries),
      this.currentPackage$,
    ])
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe(([kind, translations, countries]) => {
        if (!kind) {
          return;
        }

        if (kind === ProcessType.TRAVELINPL) {
          const dateFrom = this.persistStorage.getJson<FormsTravelInPl>(PersistStorageKeys.FormsTravelInPl)?.travelDates?.dateFrom || null;
          const dateTo = this.persistStorage.getJson<FormsTravelInPl>(PersistStorageKeys.FormsTravelInPl)?.travelDates?.dateTo || null;
          const numberOfInsureds =
            this.persistStorage.getJson<FormsTravelInPl>(PersistStorageKeys.FormsTravelInPl)?.numberOfInsureds || null;
          const insuredsBirthYear =
            this.persistStorage.getJson<FormsTravelInPl>(PersistStorageKeys.FormsTravelInPl)?.insuredBirthYear || null;
          const winterSportsHiRisk =
            this.persistStorage.getJson<FormsTravelInPl>(PersistStorageKeys.FormsTravelInPl)?.winterSportsHiRisk || null;
          const winterSportsHiRiskForAllInsureds =
            this.persistStorage.getJson<FormsTravelInPl>(PersistStorageKeys.FormsTravelInPl)?.winterSportsHiRiskForAllInsureds || false;
          const personsWithWinterSportsHiRiskArray =
            this.persistStorage.getJson<FormsTravelInPl>(PersistStorageKeys.FormsTravelInPl)?.personsWithWinterSportsHiRisk || false;
          const personsWithWinterSportsHiRisk: string[] = [];

          this.offerDateParams$.next({ dateFrom, dateTo });

          this.informationsAboutJourney = [
            {
              title: objectGetProp(translations, 'modals.offer-leaflet.destination.title') as string,
              value: objectGetProp(translations, 'summary.poland') as string,
            },
            {
              title: objectGetProp(translations, `modals.offer-leaflet.travelDates.title`) as string,
              value: `${dateFormatter(dateFrom as string)} - ${dateFormatter(dateTo as string)}`,
            },
            {
              title: objectGetProp(translations, `modals.offer-leaflet.numberOfInsureds.title`) as string,
              value: numberOfInsureds ? String(numberOfInsureds) : '',
            },
            {
              title: objectGetProp(translations, `modals.offer-leaflet.insuredsBirthYear.title`) as string,
              value: insuredsBirthYear?.join(', '),
            },
          ];

          if (winterSportsHiRisk && personsWithWinterSportsHiRiskArray && insuredsBirthYear && !winterSportsHiRiskForAllInsureds) {
            personsWithWinterSportsHiRiskArray.forEach((item, index) => {
              if (item) {
                personsWithWinterSportsHiRisk.push(String(insuredsBirthYear[index]));
              }
            });
          }

          if (winterSportsHiRiskForAllInsureds) {
            personsWithWinterSportsHiRisk.push(
              objectGetProp(translations, 'modals.offer-leaflet.winterSportsHiRisk.options.all') as string,
            );
          }

          this.informationsAboutJourney.push({
            title: objectGetProp(translations, `modals.offer-leaflet.winterSportsHiRisk.title`) as string,
            value:
              (objectGetProp(translations, winterSportsHiRisk ? 'common.YES' : 'common.NO') as string) +
              (winterSportsHiRisk ? ` (${personsWithWinterSportsHiRisk.join(', ')})` : ''),
          });

          if (this.isAppAgentOrTravelAgency) {
            const withExtremeSports =
              this.persistStorage.getJson<FormsTravelInPl>(PersistStorageKeys.FormsTravelInPl)?.withExtremeSports || null;
            const extremeSportsForAllInsureds =
              this.persistStorage.getJson<FormsTravelInPl>(PersistStorageKeys.FormsTravelInPl)?.extremeSportsForAllInsureds || false;
            const personsWithExtremeSportsArray =
              this.persistStorage.getJson<FormsTravelInPl>(PersistStorageKeys.FormsTravelInPl)?.personsWithExtremeSports || null;
            const personsWithExtremeSports: string[] = [];

            if (withExtremeSports && personsWithExtremeSportsArray && insuredsBirthYear && !extremeSportsForAllInsureds) {
              personsWithExtremeSportsArray.forEach((item, index) => {
                if (item) {
                  personsWithExtremeSports.push(String(insuredsBirthYear[index]));
                }
              });
            }

            if (extremeSportsForAllInsureds) {
              personsWithExtremeSports.push(objectGetProp(translations, 'modals.offer-leaflet.withExtremeSports.options.all') as string);
            }

            this.informationsAboutJourney.push({
              title: objectGetProp(translations, `modals.offer-leaflet.withExtremeSports.title`) as string,
              value:
                (objectGetProp(translations, withExtremeSports ? 'common.YES' : 'common.NO') as string) +
                (withExtremeSports ? ` (${personsWithExtremeSports.join(', ')})` : ''),
            });
          }
        } else if (kind === ProcessType.TRIPABROAD) {
          const destination = this.persistStorage.getJson<FormsTripAbroad>(PersistStorageKeys.FormsTripAbroad)?.destination || null;
          const destinationId = parseInt(destination?.replace('H', '') as string, 10);
          const zone = getCountryZone(destination, countries);
          const dateFrom = this.persistStorage.getJson<FormsTripAbroad>(PersistStorageKeys.FormsTripAbroad)?.travelDates?.dateFrom || null;
          const dateTo = this.persistStorage.getJson<FormsTripAbroad>(PersistStorageKeys.FormsTripAbroad)?.travelDates?.dateTo || null;
          const numberOfInsureds =
            this.persistStorage.getJson<FormsTripAbroad>(PersistStorageKeys.FormsTripAbroad)?.numberOfInsureds || null;
          const insuredsBirthYear =
            this.persistStorage.getJson<FormsTripAbroad>(PersistStorageKeys.FormsTripAbroad)?.insuredBirthYear || null;
          const winterSportsHiRisk =
            this.persistStorage.getJson<FormsTripAbroad>(PersistStorageKeys.FormsTripAbroad)?.winterSportsHiRisk || null;
          const winterSportsHiRiskForAll =
            this.persistStorage.getJson<FormsTripAbroad>(PersistStorageKeys.FormsTripAbroad)?.winterSportsHiRiskForAllInsureds || null;
          const personsWithWinterSportsHiRiskArray =
            this.persistStorage.getJson<FormsTripAbroad>(PersistStorageKeys.FormsTripAbroad)?.personsWithWinterSportsHiRisk || false;
          const personsWithWinterSportsHiRisk: string[] = [];

          this.offerDateParams$.next({ dateFrom, dateTo });

          this.informationsAboutJourney = [
            {
              title: objectGetProp(translations, `modals.offer-leaflet.destination.title`) as string,
              value: countries?.find((country) => country.countryId === destinationId)?.countryName,
              ...(zone
                ? {
                    description: objectGetProp(
                      translations,
                      `modals.offer-leaflet.destination.additionalInfo.${zone.toLowerCase()}`,
                    ) as string,
                  }
                : {}),
            },
            {
              title: objectGetProp(translations, `modals.offer-leaflet.travelDates.title`) as string,
              value: `${dateFormatter(dateFrom as string)} - ${dateFormatter(dateTo as string)}`,
            },
            {
              title: objectGetProp(translations, `modals.offer-leaflet.numberOfInsureds.title`) as string,
              value: numberOfInsureds ? String(numberOfInsureds) : '',
            },
            {
              title: objectGetProp(translations, `modals.offer-leaflet.insuredsBirthYear.title`) as string,
              value: insuredsBirthYear?.join(', '),
            },
          ];

          if (winterSportsHiRisk && personsWithWinterSportsHiRiskArray && insuredsBirthYear && !winterSportsHiRiskForAll) {
            personsWithWinterSportsHiRiskArray.forEach((item, index) => {
              if (item) {
                personsWithWinterSportsHiRisk.push(String(insuredsBirthYear[index]));
              }
            });
          }

          if (winterSportsHiRiskForAll) {
            personsWithWinterSportsHiRisk.push(
              objectGetProp(translations, 'modals.offer-leaflet.winterSportsHiRisk.options.all') as string,
            );
          }

          this.informationsAboutJourney.push({
            title: objectGetProp(translations, `modals.offer-leaflet.winterSportsHiRisk.title`) as string,
            value:
              (objectGetProp(translations, winterSportsHiRisk ? 'common.YES' : 'common.NO') as string) +
              (winterSportsHiRisk ? ` (${personsWithWinterSportsHiRisk.join(', ')})` : ''),
          });

          if (this.isAppAgentOrTravelAgency) {
            const withExtremeSports =
              this.persistStorage.getJson<FormsTripAbroad>(PersistStorageKeys.FormsTripAbroad)?.withExtremeSports || null;
            const personsWithExtremeSportsArray =
              this.persistStorage.getJson<FormsTripAbroad>(PersistStorageKeys.FormsTripAbroad)?.personsWithExtremeSports || null;
            const extremeSportsForAllInsureds =
              this.persistStorage.getJson<FormsTripAbroad>(PersistStorageKeys.FormsTripAbroad)?.extremeSportsForAllInsureds || false;
            const personsWithExtremeSports: string[] = [];

            if (withExtremeSports && personsWithExtremeSportsArray && insuredsBirthYear && !extremeSportsForAllInsureds) {
              personsWithExtremeSportsArray.forEach((item, index) => {
                if (item) {
                  personsWithExtremeSports.push(String(insuredsBirthYear[index]));
                }
              });
            }

            if (extremeSportsForAllInsureds) {
              personsWithExtremeSports.push(objectGetProp(translations, 'modals.offer-leaflet.withExtremeSports.options.all') as string);
            }

            this.informationsAboutJourney.push({
              title: objectGetProp(translations, `modals.offer-leaflet.withExtremeSports.title`) as string,
              value:
                (objectGetProp(translations, withExtremeSports ? 'common.YES' : 'common.NO') as string) +
                (withExtremeSports ? ` (${personsWithExtremeSports.join(', ')})` : ''),
            });
          }
        } else if (ProcessType.RESIGNATION) {
          const dateFrom =
            this.persistStorage.getJson<FormsResignation>(PersistStorageKeys.FormsResignation)?.travelDates?.dateFrom || null;
          const dateTo = this.persistStorage.getJson<FormsResignation>(PersistStorageKeys.FormsResignation)?.travelDates?.dateTo || null;
          const numberOfInsureds =
            this.persistStorage.getJson<FormsResignation>(PersistStorageKeys.FormsResignation)?.numberOfInsureds || null;

          this.offerDateParams$.next({ dateFrom, dateTo });

          this.informationsAboutJourney = [
            {
              title: objectGetProp(translations, `modals.offer-leaflet.travelDates.title`) as string,
              value: `${dateFormatter(dateFrom as string)} - ${dateFormatter(dateTo as string)}`,
            },
            {
              title: objectGetProp(translations, `modals.offer-leaflet.numberOfInsureds.title`) as string,
              value: numberOfInsureds ? String(numberOfInsureds) : '',
            },
          ];
        }
      });
  }

  calculateColumnsWidth() {
    this.packageTableProps$.subscribe((packageProps) => {
      const packagesLength = [packageProps.labelComfort, packageProps.labelExtra, packageProps.labelMax, packageProps.labelPlus].filter(
        (item) => item,
      ).length;
      this.numberOfPackages = Array(packagesLength).fill(ZERO);

      switch (packagesLength) {
        case FOUR_PACKAGES:
          {
            this.packageColumnsWidth = '2';
            this.packageHeaderColumnsWidth = '4';
          }
          break;
        case THREE_PACKAGES:
          {
            this.packageColumnsWidth = '3';
            this.packageHeaderColumnsWidth = '3';
          }
          break;
        case TWO_PACKAGES:
          {
            this.packageColumnsWidth = '4';
            this.packageHeaderColumnsWidth = '4';
          }
          break;
        case ONE_PACKAGE:
          {
            this.packageColumnsWidth = '7';
            this.packageHeaderColumnsWidth = '5';
          }
          break;
        default: {
          this.packageColumnsWidth = '2';
          this.packageHeaderColumnsWidth = '2';
        }
      }
    });
  }

  getPremiumValue(
    quotationPackage: QuotationPackage,
    premiumCalculationState: ApiState,
    premiumCalculation: Record<string, PremiumCalculationApi>,
  ) {
    switch (premiumCalculationState) {
      case ApiState.success: {
        const { amount } = quotationPackage.totalValue;
        const premiumAmount = premiumCalculation[quotationPackage.codeName]?.result.premium;

        return formatterCurrency(premiumAmount || amount, quotationPackage.totalValue.currency);
      }
      default:
        return '';
    }
  }

  isAddonActive(addon: QuotationAddon, packageAddons: SwitcherStates) {
    if (packageAddons.state[addon.codeName]) {
      return true;
    }

    return false;
  }
}
